import { useEffect, useRef, useState } from 'react';

// Themed replacement for a native <select>. `options` is [{ value, label }];
// an empty-string value represents the "all" option.
export default function FilterDropdown({ value, options, onChange, ariaLabel, className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function selectOption(nextValue) {
    setOpen(false);
    if (nextValue !== value) onChange(nextValue);
  }

  return (
    <div className={`filter-dropdown ${open ? 'open' : ''} ${className}`} ref={wrapRef}>
      <button
        type="button"
        className={`filter-dropdown-trigger ${value ? 'has-value' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="filter-dropdown-label">{selected?.label}</span>
        <svg className="filter-dropdown-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="filter-dropdown-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value || 'all'} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`filter-dropdown-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => selectOption(option.value)}
                >
                  <span>{option.label}</span>
                  {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
